import { sessionStorage as safeSessionStorage } from 'utils/browserStorage';
import React, { FC, useState, useEffect } from "react";
import {
    OptimizerContainer,
    OptimizerTitle,
    OptimizationItem,
    StatusIcon,
    ItemContent,
    ItemTitle,
    ItemDescription,
    ActionContainer,
    FixNowButton,
} from "./ShiftOptimizer.styles";
import { Box, Typography } from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { ShiftDataProps } from "../PostJobPage/PostJob";
import { Type } from "utils/types";
import { Analytics } from "utils/analytics";
import { RateBenchmarkResponse } from "api/Api";
import { getClampedRecommendedRate, getRateGuidanceCopy, getRateGuidanceState } from "utils/rateGuidance";

interface ShiftOptimizerProps {
    shift: ShiftDataProps;
    role: Type;
    rateBenchmark?: RateBenchmarkResponse | null;
    onEdit: () => void;
    onOptimizationApplied?: (updates: Partial<ShiftDataProps>) => void;
    showBoostItem?: boolean;
    boostAvailability?: { status: 'available' | 'queued' | 'unavailable'; activeCount: number; roleType: 0 | 1 } | null;
    shiftId?: number | string;
    onBoostRestricted?: () => void;
}

interface OptimizationItem {
    id: string;
    isOptimized: boolean;
    title: string;
    optimizedDescription: React.ReactNode;
    improvementDescription: React.ReactNode;
    editable: boolean;
    visible: boolean;
}

const SHIFT_TRACKING_ID_KEY = 'ts_shift_tracking_id';
const getOrInitShiftTrackingId = (): string => {
    try {
        const existing = safeSessionStorage.getItem(SHIFT_TRACKING_ID_KEY);
        if (existing) return existing;
        const randomPart = Math.random().toString(36).slice(2, 10);
        const created = `st_${Date.now().toString(36)}_${randomPart}`;
        safeSessionStorage.setItem(SHIFT_TRACKING_ID_KEY, created);
        return created;
    } catch {
        const randomPart = Math.random().toString(36).slice(2, 10);
        return `st_${Date.now().toString(36)}_${randomPart}`;
    }
};

const trackFixIt = (
    setting: 'rate_competitiveness' | 'instabook' | 'allow_partial_offers' | 'boost_this_shift' | 'downtime_payment',
    options?: { boost_level?: string | number, shiftId?: number | string }
) => {
    try {
        const trackingId = getOrInitShiftTrackingId();
        const payload: Record<string, unknown> = {
            source_page: 'review_shift_details',
            ui_element: 'button',
            setting,
            previous_enabled: false,
            result_enabled: true,
            shift_tracking_id: trackingId,
        };
        if (options?.shiftId) payload.shift_id = options.shiftId;
        if (options?.boost_level !== undefined) payload.boost_level = options.boost_level;
        Analytics.track('shift_optimizer_fix_it_click', payload);
    } catch (_) {}
};

const ShiftOptimizer: FC<ShiftOptimizerProps> = ({
    shift,
    role,
    rateBenchmark,
    onEdit,
    onOptimizationApplied,
    showBoostItem = true,
    boostAvailability,
    shiftId,
    onBoostRestricted,
}) => {
    const [optimizationItems, setOptimizationItems] = useState<OptimizationItem[]>([]);

    useEffect(() => {
        // Analyze shift data and create optimization items
        const items: OptimizationItem[] = [];
        const rateCompetitivenessState = getRateGuidanceState(shift.proposedRate, rateBenchmark ?? null);
        const rateGuidanceCopy = getRateGuidanceCopy(rateCompetitivenessState, rateBenchmark ?? null);
        const recommendedRate = getClampedRecommendedRate(rateBenchmark, role as 0 | 1);

        if (rateCompetitivenessState && recommendedRate !== undefined) {
            items.push({
                id: 'rateCompetitiveness',
                isOptimized: rateCompetitivenessState !== 'low',
                title: 'Rate Competitiveness',
                optimizedDescription: rateCompetitivenessState === 'strong'
                    ? (rateGuidanceCopy?.description || 'Your proposed rate is above the competitive range for similar shifts and gives this shift a stronger chance of getting offers quickly.')
                    : (rateGuidanceCopy?.description || 'Your proposed rate is aligned with the competitive range for similar shifts.'),
                improvementDescription: (
                    <>
                        {rateGuidanceCopy?.description || 'This proposed rate is low for this shift.'}
                        {rateGuidanceCopy?.historicalContext
                            ? <> {rateGuidanceCopy.historicalContext}</>
                            : null}
                        {' '}Consider raising your proposed rate to about <strong>${recommendedRate}/hr</strong>.
                    </>
                ),
                editable: true,
                visible: true
            });
        }

        // 1. Instabook Analysis
        items.push({
            id: 'instaBook',
            isOptimized: shift.instaBook,
            title: 'Instabook',
            optimizedDescription: 'Instabook is enabled. This increases your chances of booking by 40% and attracts top-rated professionals.',
            improvementDescription: 'Instabook is disabled. Enable it to significantly increase your chances of booking, especially with top-rated professionals.',
            editable: true,
            visible: true
        });

        // 2. Allow Partial Offers Analysis
        // allowPartialOffers is passed from PostJob form data
        items.push({
            id: 'partialOffers',
            isOptimized: shift.allowPartialOffers ?? true, // Default to true if not specified
            title: 'Allow Partial Offers',
            optimizedDescription: 'You are allowing partial offers. This provides flexibility and increases the chances of filling your shift.',
            improvementDescription: 'Consider allowing partial offers. It provides more flexibility and increases booking chances, especially for last-minute shifts.',
            editable: true,
            visible: true
        });

        // 3. Boost selection (finalize like other optimizer elements) — only show when allowed
        if (showBoostItem) {
            const boostSelected = shift.boost === true;
            items.push({
                id: 'boost',
                isOptimized: boostSelected,
                title: 'Boost This Shift',
                optimizedDescription: (
                    <>
                        {boostAvailability && (boostAvailability.status === 'queued' || boostAvailability.status === 'unavailable') ? (
                            <>Boosted Post slots are all filled in your area, but don't worry — spots open up quickly. We'll automatically queue your shift and notify you as soon as it becomes Boosted. You will not be charged unless your shift becomes Boosted and you receive your first offer.</>
                        ) : (
                            <>This shift is Boosted — highlighting your shift to top talent and giving you the best chance of booking your shift quickly.</>
                        )}
                    </>
                ),
                improvementDescription: (
                    <>
                        You haven't Boosted this post. Boost it now to optimize your chances of booking top talent and filling your shift faster.<br />
                        <a
                            href={'https://support.tempstars.com/hc/en-us/articles/44030299286035-Boosted-Post-Terms-of-Service'}
                            target="_system"
                            className={"external"}
                        >
                            By Boosting this shift, you agree to TempStars Boosted Post Terms of Service
                        </a>
                    </>
                ),
                editable: true,
                visible: true,
            });
        }

        // 4. Downtime Unpaid Analysis (Hygienists only)
        if (role === Type.Hygienist) {
            // downtimeUnpaid is passed from PostJob form data
            const downtimeUnpaid = shift.downtimeUnpaid ?? false;
            items.push({
                id: 'downtime',
                isOptimized: !downtimeUnpaid,
                title: 'Downtime Payment',
                optimizedDescription: 'You are paying for downtime. Professionals appreciate this and it attracts quality talent.',
                improvementDescription: 'Downtime is marked as unpaid. This significantly reduces offers from top professionals.',
                editable: true,
                visible: true
            });
        }

        setOptimizationItems(items);
    }, [shift, role, rateBenchmark, showBoostItem, boostAvailability]);

    const handleFixNow = (itemId: string) => {
        if (itemId === 'boost' && onBoostRestricted) {
            onBoostRestricted();
            return;
        }

        const updates: Partial<ShiftDataProps> = {};
        const recommendedRate = getClampedRecommendedRate(rateBenchmark, role as 0 | 1);
        
        switch (itemId) {
            case 'rateCompetitiveness':
                if (recommendedRate !== undefined) {
                    updates.proposedRate = recommendedRate;
                }
                break;
            case 'instaBook':
                updates.instaBook = true;
                break;
            case 'partialOffers':
                updates.allowPartialOffers = true;
                break;
            case 'downtime':
                updates.downtimeUnpaid = false;
                break;
            case 'boost':
                updates.boost = true;
                break;
        }

        // Update local state immediately for UI feedback
        setOptimizationItems(prevItems =>
            prevItems.map(item =>
                item.id === itemId ? { ...item, isOptimized: true } : item
            )
        );

        // Notify parent component of changes
        if (onOptimizationApplied) {
            onOptimizationApplied(updates);
        }

        const settingMap: Record<string, 'rate_competitiveness' | 'instabook' | 'allow_partial_offers' | 'boost_this_shift' | 'downtime_payment'> = {
            rateCompetitiveness: 'rate_competitiveness',
            instaBook: 'instabook',
            partialOffers: 'allow_partial_offers',
            boost: 'boost_this_shift',
            downtime: 'downtime_payment',
        };
        const mapped = settingMap[itemId];
        if (mapped) {
            trackFixIt(mapped, { shiftId });
        }
    };

    const hasImprovements = optimizationItems.some(item => !item.isOptimized && item.editable);

    return (
        <OptimizerContainer>
            <OptimizerTitle>Shift Optimizer</OptimizerTitle>
            
            {hasImprovements && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#FFF3CD', borderRadius: 1, border: '1px solid #FFEAA7' }}>
                    <Typography variant="body2" color="textSecondary">
                        We've identified opportunities to improve your shift posting
                    </Typography>
                </Box>
            )}

            {optimizationItems.map((item) => (
                <OptimizationItem key={item.id}>
                    <StatusIcon status={item.isOptimized ? 'optimized' : 'improvement'}>
                        {item.isOptimized ? <CheckCircleOutline /> : <WarningAmberOutlined />}
                    </StatusIcon>
                    <ItemContent>
                        <ItemTitle>{item.title}</ItemTitle>
                        <ItemDescription>
                            {item.isOptimized ? item.optimizedDescription : item.improvementDescription}
                        </ItemDescription>
                        {item.editable && !item.isOptimized && (
                            <ActionContainer>
                                <FixNowButton onClick={() => handleFixNow(item.id)}>
                                    Fix Now
                                </FixNowButton>
                            </ActionContainer>
                        )}
                    </ItemContent>
                </OptimizationItem>
            ))}
        </OptimizerContainer>
    );
};

export default ShiftOptimizer;